import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import type {
  LoginRequest,
  MfaEnrollmentResponse,
  MfaSetupResponse,
  SessionResponse,
} from "@mongmee/contracts";

import { ApiError, apiRequest, subscribeToAuthenticationRequired } from "./api";
import { useLocation } from "./router";
import { workspaceAudience, type AuthAudience } from "./auth-workspace";

export type { AuthAudience } from "./auth-workspace";

interface AuthContextValue {
  session: SessionResponse | null;
  loading: boolean;
  initializationError: unknown;
  audience: AuthAudience;
  authApiBasePath: "/v1/auth" | "/v1/platform-auth";
  login: (input: LoginRequest) => Promise<SessionResponse>;
  startMfaSetup: () => Promise<MfaSetupResponse>;
  confirmMfaEnrollment: (code: string, rememberDevice?: boolean) => Promise<string[]>;
  verifyMfa: (code: string, rememberDevice?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const audience = workspaceAudience(location.pathname);
  // Remount before rendering the other workspace: no stale session/CSRF state.
  return <AudienceAuthProvider key={audience} audience={audience}>{children}</AudienceAuthProvider>;
}

function AudienceAuthProvider({ children, audience }: PropsWithChildren<{ audience: AuthAudience }>) {
  const queryClient = useQueryClient();
  const authApiBasePath = audience === "platform"
    ? "/v1/platform-auth" as const
    : "/v1/auth" as const;
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializationError, setInitializationError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    try {
      const nextSession = await apiRequest<SessionResponse>(`${authApiBasePath}/session`);
      setSession(nextSession);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSession(null);
        return;
      }
      throw error;
    }
  }, [authApiBasePath]);

  useEffect(() => subscribeToAuthenticationRequired((path) => {
    const failedAudience = path === "/v1/platform" || path.startsWith("/v1/platform/") || path.startsWith("/v1/platform-auth/") ? "platform" : "customer";
    if (failedAudience !== audience) return;
    void queryClient.cancelQueries();
    queryClient.clear();
    setSession(null);
    setInitializationError(null);
  }), [audience, queryClient]);

  useEffect(() => {
    void queryClient.cancelQueries();
    queryClient.clear();
    setLoading(true);
    setSession(null);
    setInitializationError(null);
    void refresh()
      .catch((error: unknown) => setInitializationError(error))
      .finally(() => setLoading(false));
  }, [queryClient, refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      initializationError,
      audience,
      authApiBasePath,
      async login(input) {
        const nextSession = await apiRequest<SessionResponse>(`${authApiBasePath}/login`, {
          method: "POST",
          body: JSON.stringify(input),
        });
        setSession(nextSession);
        return nextSession;
      },
      async startMfaSetup() {
        if (!session) throw new Error("Authentication is required.");
        return apiRequest<MfaSetupResponse>(
          `${authApiBasePath}/mfa/totp/setup`,
          { method: "POST" },
          session.csrfToken,
        );
      },
      async confirmMfaEnrollment(code, rememberDevice = false) {
        if (!session) throw new Error("Authentication is required.");
        const result = await apiRequest<MfaEnrollmentResponse>(
          `${authApiBasePath}/mfa/totp/confirm`,
          { method: "POST", body: JSON.stringify({ code, rememberDevice }) },
          session.csrfToken,
        );
        setSession(result.session);
        return result.recoveryCodes;
      },
      async verifyMfa(code, rememberDevice = false) {
        if (!session) throw new Error("Authentication is required.");
        const nextSession = await apiRequest<SessionResponse>(
          `${authApiBasePath}/mfa/verify`,
          { method: "POST", body: JSON.stringify({ code, rememberDevice }) },
          session.csrfToken,
        );
        setSession(nextSession);
      },
      async logout() {
        if (!session) return;
        await apiRequest<void>(
          `${authApiBasePath}/logout`,
          { method: "POST" },
          session.csrfToken,
        );
        setSession(null);
      },
      refresh,
    }),
    [audience, authApiBasePath, initializationError, loading, refresh, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
